import { useCallback, useMemo, useRef } from 'react';

import {
  ASSESSMENT_ACTIVITIES,
  LanguageCodes,
  LESSON_ACTIVITIES,
} from 'global/constants';
import { InquisitiveActivitiesContentType } from 'global/graphql/scalars.type';
import { getSectionsAndSubSectionsOverview } from '../../activity-builder.utils';
import { InvisiblePrintWrapper } from '../../print/activity-builder-print-generator.style';
import { INTRODUCTION_INDEX, LANDSCAPE } from '../../activity-builder.apollo';
import { useGetActivitySections } from '../../activity-builder-section.hooks';
import { ActivityBuilderPrintGenerator } from '../../print/activity-builder-print-generator';
import { AssessmentBuilderPrintGenerator } from '../../assessment/print/assessment-builder-print-generator';
import { useGetLessonActivities } from 'approot/activity-builder/activity-builder.hooks';
import { useGetAssessmentActivities } from 'approot/activity-builder/assessment/assessment-builder.hooks';

const RENDER_CONTAINER_ID = 'prerender';

type Props = {
  contentId: number;
  contentType: InquisitiveActivitiesContentType;
  classId: number | null;
  version: string;
  language: LanguageCodes;
  onRenderComplete: (contentString: string) => void;
};

const SettingsGeneratePrintContent = ({
  contentId,
  contentType,
  classId,
  language,
  version,
  onRenderComplete,
}: Props) => {
  const completedCount = useRef<number>(0);

  const { data: lessonActivitiesData } = useGetLessonActivities({
    skip: contentType === ASSESSMENT_ACTIVITIES,
    fetchPolicy: 'network-only',
    language,
    contentId,
    classId,
  });

  const { data: assessmentActivitiesData } = useGetAssessmentActivities({
    skip: contentType === LESSON_ACTIVITIES,
    fetchPolicy: 'network-only',
    language,
    contentId,
    classId,
  });

  const activities =
    contentType === LESSON_ACTIVITIES
      ? lessonActivitiesData
      : assessmentActivitiesData;

  const { data: sectionsData } = useGetActivitySections({
    contentId,
    contentType,
    classId,
    sectionIds: activities?.sectionsOverview.map(s => s.id),
    skip: !activities?.sectionsOverview,
    language,
  });

  const sections = sectionsData?.getActivitiesSections;

  const slides = useMemo(() => {
    return [
      INTRODUCTION_INDEX,
      ...(activities?.sectionsOverview
        ? getSectionsAndSubSectionsOverview(activities.sectionsOverview).map(
            (_, index) => index
          )
        : []),
    ];
  }, [activities?.sectionsOverview]);

  const onComplete = useCallback(async () => {
    const generate = await import('../../print/activity-builder-print');
    const container = document.getElementById(RENDER_CONTAINER_ID);
    if (container && sections && activities) {
      const contentString =
        activities.__typename === 'LessonActivities'
          ? generate.renderAndCollectStyles(
              ActivityBuilderPrintGenerator({
                sections,
                interactiveLesson: activities,
                slidesPerPage: 1,
                orientation: 'landscape',
                slides,
                version,
                language,
              }),
              container.outerHTML
            )
          : generate.renderAndCollectStyles(
              AssessmentBuilderPrintGenerator({
                sections,
                interactiveAssessment: activities,
                slidesPerPage: 1,
                orientation: 'landscape',
                slides,
                version,
                language,
              }),
              container.outerHTML
            );
      onRenderComplete(contentString);
    }
  }, [sections, activities, version, language, slides, onRenderComplete]);

  // All children have to have rendered and resized before we can call the final callback
  const handleChildCallback = (totalPages: number) => {
    completedCount.current += 1;
    if (completedCount.current === totalPages) {
      onComplete();
    }
  };

  return (
    <>
      <InvisiblePrintWrapper id={RENDER_CONTAINER_ID}>
        {activities &&
          activities.__typename === 'LessonActivities' &&
          sections && (
            <ActivityBuilderPrintGenerator
              interactiveLesson={activities}
              sections={sections}
              slidesPerPage={1}
              orientation={LANDSCAPE}
              slides={slides}
              version={version}
              language={language}
              onRenderComplete={handleChildCallback}
            />
          )}
        {activities &&
          activities.__typename === 'AssessmentActivities' &&
          sections && (
            <AssessmentBuilderPrintGenerator
              interactiveAssessment={activities}
              sections={sections}
              slidesPerPage={1}
              orientation={LANDSCAPE}
              slides={slides}
              version={version}
              language={language}
              onRenderComplete={handleChildCallback}
            />
          )}
      </InvisiblePrintWrapper>
    </>
  );
};

export default SettingsGeneratePrintContent;
